import { useEffect, useRef, useState } from "react";

// Vidéo d'ambiance du moteur "couture_v1" — règle CLIENT stricte (README du
// handoff) : lecture automatique, en boucle, dans la page (`playsInline`),
// TOUJOURS muette, jamais de son, sans contrôles.
//
// Les attributs ne suffisent pas : Chrome ne respecte pas l'autoplay sur un
// élément inséré après le chargement et met en pause les vidéos sorties de
// l'écran. D'où, côté JS : `muted`/`volume` forcés au montage, à `play`, à
// `volumechange` et après une `pause` (relance après 150 ms), plus un
// IntersectionObserver (seuil 0,05) qui relance la lecture quand la vidéo
// revient dans la fenêtre.
//
// Spécificités MOBILE :
//   * iPhone en mode Économie d'énergie (et certains Android) : la lecture
//     automatique est refusée (`play()` rejeté). La lecture reprend alors au
//     premier geste du visiteur sur la page (toucher, clic, touche) — un
//     geste autorise la lecture même dans ce mode.
//   * erreur réseau passagère (coupure, 503) : la vidéo ne retentait jamais
//     et restait noire. Elle est rechargée, avec un délai croissant ; le
//     navigateur affiche l'image de repli (`poster`) tant qu'il n'a aucune
//     image de la vidéo.
//   * `prefers-reduced-motion: reduce` (réglage « Réduire les animations ») :
//     pas de lecture. On montre l'image de repli si elle existe, sinon la
//     PREMIÈRE IMAGE de la vidéo (fragment `#t=0.1`, que Safari iOS affiche
//     sans lecture) — jamais un bandeau vide.
const MAX_RETRIES = 4;

function useReducedMotion() {
  const [reduce, setReduce] = useState(() =>
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(prefers-reduced-motion: reduce)").matches
      : false
  );
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (e) => setReduce(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return reduce;
}

const silence = (v) => {
  v.muted = true;
  v.defaultMuted = true;
  v.volume = 0;
  // Attribut ET propriété : React ne pose que la propriété `muted`, alors que
  // Safari iOS vérifie aussi l'attribut pour autoriser la lecture automatique.
  v.setAttribute("muted", "");
  v.removeAttribute("controls");
};

// Ajoute un fragment de temps à l'URL (première image affichée sans lecture).
const withFirstFrame = (src) => (src && !src.includes("#") ? `${src}#t=0.1` : src);

export default function MutedVideo({ src, poster = "", className = "", style, opacity }) {
  const ref = useRef(null);
  const reduce = useReducedMotion();

  useEffect(() => {
    const v = ref.current;
    if (!v || reduce) return undefined;
    let disposed = false;
    let retries = 0;
    let pauseTimer = 0;
    let retryTimer = 0;
    let waitingForGesture = false;

    const onGesture = () => {
      removeGestureListeners();
      if (!disposed) play();
    };
    const gestureEvents = ["touchend", "click", "keydown"];
    const addGestureListeners = () => {
      if (waitingForGesture) return;
      waitingForGesture = true;
      gestureEvents.forEach((ev) => document.addEventListener(ev, onGesture, { passive: true, capture: true }));
    };
    const removeGestureListeners = () => {
      if (!waitingForGesture) return;
      waitingForGesture = false;
      gestureEvents.forEach((ev) => document.removeEventListener(ev, onGesture, { capture: true }));
    };

    function play() {
      silence(v);
      const p = v.play();
      if (p && p.catch) {
        p.catch((err) => {
          // Lecture automatique refusée par le système : on attend un geste.
          if (err && err.name === "NotAllowedError") addGestureListeners();
        });
      }
    }

    silence(v);
    const onVolume = () => silence(v);
    const onPlay = () => {
      silence(v);
      removeGestureListeners();
    };
    // Des images défilent réellement : la vidéo est saine, le compteur de
    // nouvelles tentatives repart de zéro (et non à `play`, émis même quand
    // le chargement va échouer — ce qui relancerait sans fin).
    const onPlaying = () => {
      retries = 0;
    };
    const onPause = () => {
      window.clearTimeout(pauseTimer);
      pauseTimer = window.setTimeout(() => {
        // Jamais pendant une erreur : c'est `onError` qui gère la reprise.
        if (!disposed && v.isConnected && v.paused && !v.ended && !v.error) play();
      }, 150);
    };
    const onError = () => {
      if (retries >= MAX_RETRIES) return;
      retries += 1;
      window.clearTimeout(retryTimer);
      retryTimer = window.setTimeout(() => {
        if (disposed) return;
        v.load();
        play();
      }, 1500 * retries);
    };

    v.addEventListener("volumechange", onVolume);
    v.addEventListener("play", onPlay);
    v.addEventListener("playing", onPlaying);
    v.addEventListener("pause", onPause);
    v.addEventListener("error", onError);
    let observer = null;
    if (typeof IntersectionObserver !== "undefined") {
      observer = new IntersectionObserver(
        (entries) => entries.forEach((en) => { if (en.isIntersecting && en.target.paused && !v.error) play(); }),
        { threshold: 0.05 }
      );
      observer.observe(v);
    }
    play();
    return () => {
      disposed = true;
      window.clearTimeout(pauseTimer);
      window.clearTimeout(retryTimer);
      removeGestureListeners();
      v.removeEventListener("volumechange", onVolume);
      v.removeEventListener("play", onPlay);
      v.removeEventListener("playing", onPlaying);
      v.removeEventListener("pause", onPause);
      v.removeEventListener("error", onError);
      observer?.disconnect();
    };
  }, [src, reduce]);

  const mergedStyle = opacity === undefined ? style : { ...(style || {}), opacity };

  if (!src) {
    return poster ? <img src={poster} alt="" className={className} style={mergedStyle} /> : null;
  }
  if (reduce) {
    return poster ? (
      <img src={poster} alt="" className={className} style={mergedStyle} />
    ) : (
      <video
        src={withFirstFrame(src)}
        muted
        playsInline
        preload="metadata"
        aria-hidden="true"
        tabIndex={-1}
        className={className}
        style={mergedStyle}
      />
    );
  }
  return (
    <video
      ref={ref}
      src={src}
      poster={poster || undefined}
      autoPlay
      muted
      loop
      playsInline
      preload="metadata"
      disablePictureInPicture
      disableRemotePlayback
      aria-hidden="true"
      tabIndex={-1}
      className={className}
      style={mergedStyle}
    />
  );
}
